import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, Flex } from '@chakra-ui/react';
import { BottomControl } from '@src/components/sco/CardCarousel/Controls/BottomControl';
import { CardCarouselControlProps } from '@src/components/sco/CardCarousel/Controls/CardCarouselControlProps';
import { LeftControl } from '@src/components/sco/CardCarousel/Controls/LeftControl';
import { RightControl } from '@src/components/sco/CardCarousel/Controls/RightControl';
import { TopControl } from '@src/components/sco/CardCarousel/Controls/TopControl';
import { useInterval } from '@src/components/sco/CardCarousel/useInterval';
import { useSnapCarousel } from '@src/components/sco/CardCarousel/useSnapCarousel';
import { useContainerBreakpointValue } from '@src/components/sco/utilities/BreakpointProvider';
import { PrettyPrintJSON } from '@src/components/sco/utilities/PrettyPrintJSON';
import { useHover } from '@uidotdev/usehooks';
import moment from 'moment';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
export type CardCarouselProps = {
  activeItemIndex?: number;
  activePageIndex?: number;
  autoCycle?: boolean | CardCarouselPropsAutoCycle;
  axis?: 'x' | 'y';
  children?: ReactNode[];
  controls?: boolean | CardCarouselPropsControls;
  fade?: boolean | CardCarouselPropsFade;
  itemSize?: BoxProps['width'];
  itemHeight?: BoxProps['height'];
  onItemChange?: (itemIndex: number) => void;
  onPageChange?: (pageIndex: number) => void;
  printDebug?: boolean;
  gap?: BoxProps['padding'];
  spacing?: BoxProps['padding'];
  title?: string | ReactNode | ReactNode[];
  visibleCards?: number[];
  verticalAlign?: 'center' | 'stretch' | 'flex-start' | 'flex-end';
  horizontalAlign?: 'center' | 'stretch' | 'flex-start' | 'flex-end';
  scrollSnapAlign?: 'start' | 'end' | 'center';
  fadeInactive?: boolean | {
    activeOpacity?: number;
    inactiveOpacity?: number;
    transitionDuration?: string;
  };
  fillScreen?: boolean;
};
export type CardCarouselPropsAutoCycle = {
  delay?: number;
  disableOnClick?: boolean;
  isDisabled?: boolean;
  isPaused?: boolean;
  nextTransitionAt?: Date;
  pauseOnHover?: boolean;
  transitionType?: 'nextItem' | 'nextPage' | 'prevItem' | 'prevPage';
};
export type CardCarouselPropsControls = {
  bottom?: boolean | React.FC<CardCarouselControlProps>;
  left?: boolean | React.FC<CardCarouselControlProps>;
  right?: boolean | React.FC<CardCarouselControlProps>;
  top?: boolean | React.FC<CardCarouselControlProps>;
};
export type CardCarouselPropsFade = {
  startColor?: BoxProps['bgColor'];
  endColor?: BoxProps['bgColor'];
  maxSize?: BoxProps['maxWidth'];
};
export const CardCarousel = props => {
  const $ = _c(143);
  const axis = props.axis || "x";
  let t0;
  if ($[0] !== props.children) {
    t0 = props.children ?? [];
    $[0] = props.children;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const children = t0;
  let t1;
  if ($[2] !== children) {
    t1 = children.map(_temp);
    $[2] = children;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== axis || $[5] !== t1) {
    t2 = {
      axis,
      initialItems: t1
    };
    $[4] = axis;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const carousel = useSnapCarousel(t2);
  const {
    activeItemIndex,
    activePageIndex,
    goToItem,
    goToPage,
    items,
    itemsPercent,
    nextItem,
    nextPage,
    pages,
    pagesPercent,
    prevItem,
    prevPage,
    scrollRef
  } = carousel;
  const visibleCards = useContainerBreakpointValue(props.visibleCards) as number;
  const [hoverRef, isHovered] = useHover();
  const t3 = props.autoCycle as CardCarouselPropsAutoCycle;
  let t4;
  if ($[7] !== t3) {
    t4 = deriveAutoCycleState(t3);
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const [autoCycleState, setAutoCycleState] = useState(t4);
  const itemSize = visibleCards ? `${100 / (visibleCards || 1)}%` : props.itemSize || undefined;
  let t5;
  let t6;
  if ($[9] !== props.autoCycle) {
    t5 = () => {
      setAutoCycleState(deriveAutoCycleState(props.autoCycle as CardCarouselPropsAutoCycle));
    };
    t6 = [props.autoCycle];
    $[9] = props.autoCycle;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== activeItemIndex || $[13] !== props) {
    t7 = () => {
      if (props.onItemChange) {
        props.onItemChange(activeItemIndex);
      }
    };
    $[12] = activeItemIndex;
    $[13] = props;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== activeItemIndex) {
    t8 = [activeItemIndex];
    $[15] = activeItemIndex;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== activePageIndex || $[18] !== props) {
    t9 = () => {
      if (props.onPageChange) {
        props.onPageChange(activePageIndex);
      }
    };
    $[17] = activePageIndex;
    $[18] = props;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== activePageIndex) {
    t10 = [activePageIndex];
    $[20] = activePageIndex;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[22] !== goToPage || $[23] !== props.activePageIndex) {
    t11 = () => {
      if (typeof props.activePageIndex === "number") {
        goToPage(props.activePageIndex);
      }
    };
    t12 = [props.activePageIndex, goToPage];
    $[22] = goToPage;
    $[23] = props.activePageIndex;
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[26] !== goToItem || $[27] !== props.activeItemIndex) {
    t13 = () => {
      if (typeof props.activeItemIndex === "number") {
        goToItem(props.activeItemIndex);
      }
    };
    t14 = [props.activeItemIndex, goToItem];
    $[26] = goToItem;
    $[27] = props.activeItemIndex;
    $[28] = t13;
    $[29] = t14;
  } else {
    t13 = $[28];
    t14 = $[29];
  }
  useEffect(t13, t14);
  let t15;
  if ($[30] !== autoCycleState || $[31] !== isHovered) {
    t15 = () => {
      if (autoCycleState.pauseOnHover) {
        if (isHovered) {
          setAutoCycleState({
            ...autoCycleState,
            isPaused: true
          });
        } else {
          setAutoCycleState({
            ...autoCycleState,
            isPaused: false
          });
        }
      }
    };
    $[30] = autoCycleState;
    $[31] = isHovered;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== autoCycleState.pauseOnHover || $[34] !== isHovered) {
    t16 = [autoCycleState.pauseOnHover, isHovered];
    $[33] = autoCycleState.pauseOnHover;
    $[34] = isHovered;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  useEffect(t15, t16);
  let t17;
  if ($[36] !== autoCycleState || $[37] !== nextItem || $[38] !== nextPage || $[39] !== prevItem || $[40] !== prevPage) {
    t17 = () => {
      const transitionHandlers = {
        nextItem,
        nextPage,
        prevItem,
        prevPage
      };
      const transition = transitionHandlers[autoCycleState.transitionType];
      if (autoCycleState && !autoCycleState.isPaused && transition) {
        transition(true);
      }
      setAutoCycleState({
        ...autoCycleState,
        nextTransitionAt: moment().add(autoCycleState.delay).toDate()
      });
    };
    $[36] = autoCycleState;
    $[37] = nextItem;
    $[38] = nextPage;
    $[39] = prevItem;
    $[40] = prevPage;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  useInterval(t17, !autoCycleState.isDisabled && autoCycleState.delay || null);
  let t18;
  if ($[42] !== autoCycleState || $[43] !== carousel.hasUserClicked) {
    t18 = () => {
      if (carousel.hasUserClicked) {
        setAutoCycleState({
          ...autoCycleState,
          isPaused: true,
          isDisabled: autoCycleState.disableOnClick ? true : autoCycleState.isDisabled
        });
      }
    };
    $[42] = autoCycleState;
    $[43] = carousel.hasUserClicked;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== carousel.hasUserClicked) {
    t19 = [carousel.hasUserClicked];
    $[45] = carousel.hasUserClicked;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  useEffect(t18, t19);
  let t20;
  if ($[47] !== autoCycleState) {
    t20 = changes => {
      setAutoCycleState({
        ...autoCycleState,
        ...changes
      });
    };
    $[47] = autoCycleState;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== activeItemIndex || $[50] !== activePageIndex || $[51] !== autoCycleState || $[52] !== goToItem || $[53] !== goToPage || $[54] !== items || $[55] !== itemsPercent || $[56] !== nextItem || $[57] !== nextPage || $[58] !== pages || $[59] !== pagesPercent || $[60] !== prevItem || $[61] !== prevPage || $[62] !== props.title || $[63] !== t20) {
    t21 = {
      activeItemIndex,
      activePageIndex,
      goToItem,
      goToPage,
      items,
      itemsPercent,
      nextItem,
      nextPage,
      pages,
      pagesPercent,
      prevItem,
      prevPage,
      autoCycleState,
      title: props.title,
      updateAutoCycleState: t20
    };
    $[49] = activeItemIndex;
    $[50] = activePageIndex;
    $[51] = autoCycleState;
    $[52] = goToItem;
    $[53] = goToPage;
    $[54] = items;
    $[55] = itemsPercent;
    $[56] = nextItem;
    $[57] = nextPage;
    $[58] = pages;
    $[59] = pagesPercent;
    $[60] = prevItem;
    $[61] = prevPage;
    $[62] = props.title;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  const controlProps = t21;
  const RenderTopControl = (props.controls as CardCarouselPropsControls)?.top !== undefined ? (props.controls as CardCarouselPropsControls)?.top : props.controls !== false && TopControl;
  const RenderBottomControl = (props.controls as CardCarouselPropsControls)?.bottom !== undefined ? (props.controls as CardCarouselPropsControls).bottom : props.controls !== false && BottomControl;
  const RenderLeftControl = (props.controls as CardCarouselPropsControls)?.left !== undefined ? (props.controls as CardCarouselPropsControls).left : props.controls !== false && LeftControl;
  const RenderRightControl = (props.controls as CardCarouselPropsControls)?.right !== undefined ? (props.controls as CardCarouselPropsControls).right : props.controls !== false && RightControl;
  let t22;
  if ($[65] !== activeItemIndex || $[66] !== props.fade) {
    t22 = props.fade ? {
      content: "''",
      display: "block",
      opacity: activeItemIndex > 0 ? 1 : 0,
      pointerEvents: "none",
      position: "absolute",
      transition: "all 250ms",
      zIndex: 1
    } : {};
    $[65] = activeItemIndex;
    $[66] = props.fade;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  const beforeStyles = t22;
  let t23;
  if ($[68] !== pagesPercent || $[69] !== props.fade) {
    t23 = props.fade ? {
      content: "''",
      display: "block",
      opacity: pagesPercent < 100 ? 1 : 0,
      pointerEvents: "none",
      position: "absolute",
      transition: "all 250ms",
      zIndex: 1
    } : {};
    $[68] = pagesPercent;
    $[69] = props.fade;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  const afterStyles = t23;
  const t24 = props.fillScreen ? "flex" : "block";
  const t25 = props.fillScreen ? "column" : null;
  const t26 = props.fillScreen ? "100%" : null;
  let t27;
  if ($[71] !== RenderTopControl || $[72] !== controlProps) {
    t27 = typeof RenderTopControl === "function" && <RenderTopControl {...controlProps} />;
    $[71] = RenderTopControl;
    $[72] = controlProps;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  const t28 = props.fillScreen ? 1 : null;
  const t29 = props.fillScreen ? 0 : null;
  let t30;
  if ($[74] !== RenderLeftControl || $[75] !== controlProps || $[76] !== props.controls) {
    t30 = typeof RenderLeftControl === "function" && props.controls && typeof props.controls === "object" && props.controls.left && <Box alignItems="center" bottom={0} display="flex" height="100%" justifyContent="center" left={-4} position="absolute" top={-2} zIndex={10}><RenderLeftControl {...controlProps} /></Box>;
    $[74] = RenderLeftControl;
    $[75] = controlProps;
    $[76] = props.controls;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== RenderRightControl || $[79] !== controlProps || $[80] !== props.controls) {
    t31 = typeof RenderRightControl === "function" && props.controls && typeof props.controls === "object" && props.controls.right && <Box alignItems="center" bottom={0} display="flex" height="100%" justifyContent="center" position="absolute" right={-4} top={-2} zIndex={10}><RenderRightControl {...controlProps} /></Box>;
    $[78] = RenderRightControl;
    $[79] = controlProps;
    $[80] = props.controls;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  const t32 = axis === "x" ? "row" : "column";
  const t33 = props.fillScreen ? "100%" : undefined;
  const t34 = axis === "x" ? "auto" : undefined;
  const t35 = axis === "y" ? "scroll" : undefined;
  const t36 = axis === "x" ? "x" : "y";
  let t37;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none"
      }
    };
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== activeItemIndex || $[84] !== axis || $[85] !== children || $[86] !== itemSize || $[87] !== props.fadeInactive || $[88] !== props.horizontalAlign || $[89] !== props.itemSize || $[90] !== props.scrollSnapAlign || $[91] !== props.spacing || $[92] !== props.verticalAlign || $[93] !== props.visibleCards) {
    let t39;
    if ($[95] !== activeItemIndex || $[96] !== axis || $[97] !== itemSize || $[98] !== props.fadeInactive || $[99] !== props.horizontalAlign || $[100] !== props.itemSize || $[101] !== props.scrollSnapAlign || $[102] !== props.spacing || $[103] !== props.verticalAlign || $[104] !== props.visibleCards) {
      t39 = (child, i_0) => <Box key={i_0} alignContent={props.verticalAlign || "stretch"} alignItems={props.verticalAlign || "stretch"} data-role="carousel-item" display="flex" flexGrow={1} flexShrink={!props.visibleCards && !props.itemSize ? 1 : 0} justifyContent={props.horizontalAlign || "stretch"} justifyItems={props.horizontalAlign || "stretch"} maxWidth={axis === "x" ? itemSize : "100%"} minHeight={axis === "y" ? itemSize : undefined} paddingBottom={axis === "y" ? props.spacing : undefined} paddingRight={axis === "x" ? props.spacing : undefined} scrollSnapAlign={props.scrollSnapAlign || "start"} width="full" opacity={props.fadeInactive ? activeItemIndex === i_0 ? typeof props.fadeInactive === "object" ? props.fadeInactive.activeOpacity ?? 1 : 1 : typeof props.fadeInactive === "object" ? props.fadeInactive.inactiveOpacity ?? 0.5 : 0.5 : 1} transition={props.fadeInactive ? `opacity ${typeof props.fadeInactive === "object" ? props.fadeInactive.transitionDuration ?? "0.3s" : "0.3s"} ease` : undefined}><Box width="full">{child}</Box></Box>;
      $[95] = activeItemIndex;
      $[96] = axis;
      $[97] = itemSize;
      $[98] = props.fadeInactive;
      $[99] = props.horizontalAlign;
      $[100] = props.itemSize;
      $[101] = props.scrollSnapAlign;
      $[102] = props.spacing;
      $[103] = props.verticalAlign;
      $[104] = props.visibleCards;
      $[105] = t39;
    } else {
      t39 = $[105];
    }
    t38 = children.map(t39);
    $[83] = activeItemIndex;
    $[84] = axis;
    $[85] = children;
    $[86] = itemSize;
    $[87] = props.fadeInactive;
    $[88] = props.horizontalAlign;
    $[89] = props.itemSize;
    $[90] = props.scrollSnapAlign;
    $[91] = props.spacing;
    $[92] = props.verticalAlign;
    $[93] = props.visibleCards;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[106] !== afterStyles || $[107] !== beforeStyles || $[108] !== scrollRef || $[109] !== t32 || $[110] !== t33 || $[111] !== t34 || $[112] !== t35 || $[113] !== t36 || $[114] !== t38) {
    t39 = <Flex ref={scrollRef} _after={afterStyles} _before={beforeStyles} alignContent="stretch" alignItems="stretch" data-role="carousel-track" flex={1} flexDirection={t32} flexWrap="nowrap" height={t33} justifyContent="stretch" maxWidth="100%" overflowX={t34} overflowY={t35} scrollSnapStop="normal" scrollSnapStrictness="mandatory" scrollSnapType={t36} width="full" css={t37}>{t38}</Flex>;
    $[106] = afterStyles;
    $[107] = beforeStyles;
    $[108] = scrollRef;
    $[109] = t32;
    $[110] = t33;
    $[111] = t34;
    $[112] = t35;
    $[113] = t36;
    $[114] = t38;
    $[115] = t39;
  } else {
    t39 = $[115];
  }
  let t40;
  if ($[116] !== t28 || $[117] !== t29 || $[118] !== t30 || $[119] !== t31 || $[120] !== t39) {
    t40 = <Box data-role="carousel-inner" flex={t28} minHeight={t29} position="relative" width="full">{t30}{t31}{t39}</Box>;
    $[116] = t28;
    $[117] = t29;
    $[118] = t30;
    $[119] = t31;
    $[120] = t39;
    $[121] = t40;
  } else {
    t40 = $[121];
  }
  let t41;
  if ($[122] !== RenderBottomControl || $[123] !== controlProps) {
    t41 = typeof RenderBottomControl === "function" && <RenderBottomControl {...controlProps} />;
    $[122] = RenderBottomControl;
    $[123] = controlProps;
    $[124] = t41;
  } else {
    t41 = $[124];
  }
  let t42;
  if ($[125] !== activeItemIndex || $[126] !== activePageIndex || $[127] !== autoCycleState || $[128] !== carousel.hasUserClicked || $[129] !== itemsPercent || $[130] !== pagesPercent || $[131] !== props.fade || $[132] !== props.printDebug) {
    t42 = props.printDebug && <PrettyPrintJSON data={{
      activeItemIndex,
      activePageIndex,
      hasUserClicked: carousel.hasUserClicked,
      itemsPercent,
      pagesPercent,
      fade: props.fade,
      autoCycleState
    }} />;
    $[125] = activeItemIndex;
    $[126] = activePageIndex;
    $[127] = autoCycleState;
    $[128] = carousel.hasUserClicked;
    $[129] = itemsPercent;
    $[130] = pagesPercent;
    $[131] = props.fade;
    $[132] = props.printDebug;
    $[133] = t42;
  } else {
    t42 = $[133];
  }
  let t43;
  if ($[134] !== hoverRef || $[135] !== t24 || $[136] !== t25 || $[137] !== t26 || $[138] !== t27 || $[139] !== t40 || $[140] !== t41 || $[141] !== t42) {
    t43 = <Box ref={hoverRef} data-role="carousel-outer" display={t24} flexDirection={t25} height={t26} width="100%" zIndex={1}>{t27}{t40}{t41}{t42}</Box>;
    $[134] = hoverRef;
    $[135] = t24;
    $[136] = t25;
    $[137] = t26;
    $[138] = t27;
    $[139] = t40;
    $[140] = t41;
    $[141] = t42;
    $[142] = t43;
  } else {
    t43 = $[142];
  }
  return t43;
};
export function deriveAutoCycleState(autoCycle: CardCarouselPropsAutoCycle): CardCarouselPropsAutoCycle {
  return {
    delay: autoCycle?.delay || 3000,
    disableOnClick: autoCycle?.disableOnClick !== undefined ? autoCycle?.disableOnClick : false,
    isDisabled: !autoCycle || autoCycle?.isDisabled === false,
    isPaused: autoCycle?.pauseOnHover && !!autoCycle?.isPaused || false,
    nextTransitionAt: autoCycle?.nextTransitionAt || autoCycle?.delay ? moment().add(autoCycle.delay).toDate() : null,
    pauseOnHover: autoCycle?.pauseOnHover !== undefined ? autoCycle?.pauseOnHover : true,
    transitionType: autoCycle?.transitionType || 'nextPage'
  };
}
function _temp(_, i) {
  return i;
}